import { extend, stringToColor, strTemplate, } from "@hydrosoft/utils";
import { Circle as CircleStyle, Fill as FillStyle, Icon as IconStyle, RegularShape as OlRegularShape, Stroke as StrokeStyle, Style as OlStyle, Text as OlText, } from "ol/style";
import { asString } from "ol/color";
import { getValues } from "../data";
export var StyleType;
(function (StyleType) {
    StyleType["Marker"] = "marker";
    StyleType["Line"] = "line";
    StyleType["Polygon"] = "polygon";
})(StyleType || (StyleType = {}));
export var TextType;
(function (TextType) {
    TextType["Normal"] = "normal";
    TextType["Shorten"] = "shorten";
    TextType["Wrap"] = "wrap";
    TextType["Hide"] = "hide";
})(TextType || (TextType = {}));
export var Alignment;
(function (Alignment) {
    Alignment["Center"] = "center";
    Alignment["End"] = "end";
    Alignment["Left"] = "left";
    Alignment["Right"] = "right";
    Alignment["Start"] = "start";
})(Alignment || (Alignment = {}));
export var Baseline;
(function (Baseline) {
    Baseline["Alphabetic"] = "Alphabetic";
    Baseline["Bottom"] = "Bottom";
    Baseline["Hanging"] = "Hanging";
    Baseline["Ideographic"] = "Ideographic";
    Baseline["Middle"] = "Middle";
    Baseline["Top"] = "Top";
})(Baseline || (Baseline = {}));
export var Font;
(function (Font) {
    Font["Arial"] = "Arial";
    Font["CourierNew"] = "Courier New";
    Font["SansSerif"] = "sans-serif";
    Font["Verdana"] = "Verdana";
})(Font || (Font = {}));
export var Weight;
(function (Weight) {
    Weight["Normal"] = "normal";
    Weight["Bold"] = "bold";
})(Weight || (Weight = {}));
export var Placement;
(function (Placement) {
    Placement["Point"] = "point";
    Placement["Line"] = "line";
})(Placement || (Placement = {}));
const defaultProps = {
    template: "",
    type: TextType.Normal,
    align: Alignment.Center,
    baseline: Baseline.Middle,
    rotation: 0,
    font: Font.Arial,
    weight: Weight.Normal,
    placement: undefined,
    size: "12px",
    height: 1,
    offsetX: 0,
    offsetY: 0,
    color: [255, 0, 0],
    outline: [255, 255, 255],
    outlineWidth: 3,
};
export class Text {
    _state;
    constructor(props) {
        this._state = defaultProps;
        this._extends(props);
    }
    /**
     * Return text state
     * @returns {ITextState}
     */
    getState() {
        return this._state;
    }
    /**
     * Process template and return its result text
     * @param {TFeature} feature
     * @param {MapProvider} map
     */
    getText(feature, map) {
        const type = this._state.type;
        const minZoom = this._state.minZoom;
        const placement = this._state.placement;
        const tmplOptions = feature ? feature.properties : {};
        let text = strTemplate(this._state.template, tmplOptions);
        if (minZoom !== undefined &&
            map &&
            map.getMap().getView().getZoom() < minZoom) {
            text = "";
        }
        else if (type === TextType.Hide) {
            text = "";
        }
        else if (type === TextType.Shorten) {
            text = this._stringTrunc(text, 12);
        }
        else if (type === TextType.Wrap &&
            (!placement || placement !== Placement.Line)) {
            text = this._stringDivider(text, 16, "\n");
        }
        return text;
    }
    /**
     * Create CSS font property
     * @returns {string}
     */
    getFont() {
        const size = this._state.size;
        const height = this._state.height.toString();
        const weight = this._state.weight ? this._state.weight : "";
        const font = "'" + this._state.font.toString() + "'";
        /*if (this._state.font === FONTS.OPEN_SANS && !openSansAdded) {
            var openSans = document.createElement('link');
            openSans.href = 'https://fonts.googleapis.com/css?family=Open+Sans';
            openSans.rel = 'stylesheet';
            document.getElementsByTagName('head')[0].appendChild(openSans);
            openSansAdded = true;
        }*/
        return weight + " " + size + "/" + height + " " + font;
    }
    /**
     * Extend state by a given one
     * @param state
     */
    _extends(state) {
        if (!state) {
            return;
        }
        this._state = extend(this._state, state);
    }
    /**
     * Truncate string
     * @param {string} str
     * @param {number} n
     */
    _stringTrunc(str, n) {
        return str.length > n ? str.substr(0, n - 1) + "..." : str.substr(0);
    }
    // http://stackoverflow.com/questions/14484787/wrap-text-in-javascript
    /**
     * Divide text into multiple parts.
     * Part is created when a given string exceeds the width
     * @param {string} str
     * @param {number} width
     * @param {string} spaceReplacer
     */
    _stringDivider(str, width, spaceReplacer) {
        if (str.length > width) {
            let p = width;
            while (p > 0 && str[p] != " " && str[p] != "-") {
                p--;
            }
            if (p > 0) {
                let left;
                if (str.substring(p, p + 1) == "-") {
                    left = str.substring(0, p + 1);
                }
                else {
                    left = str.substring(0, p);
                }
                const right = str.substring(p + 1);
                return (left +
                    spaceReplacer +
                    this._stringDivider(right, width, spaceReplacer));
            }
        }
        return str;
    }
}
export const DEFAULT_DRAW_STYLE = {
    type: undefined,
    borderRadius: 8,
    color: "red",
    fill: undefined,
    fillOpacity: 0.2,
    src: undefined,
    anchor: undefined,
    width: 8,
    height: 8,
    opacity: 1,
    stroke: undefined,
    strokeOpacity: 1,
    strokeWidth: 2,
};
/**
 * Style definition for map graphic features.
 */
export class Style {
    _state;
    constructor(props = {}) {
        this._state = DEFAULT_DRAW_STYLE;
        this._extends(props);
    }
    /**
     * Return color of the fill in [r, g, b, a] type
     */
    getFillRgba(props = undefined) {
        if (props === undefined) {
            props = this._state;
        }
        const color = props.fill !== undefined ? props.fill : props.color;
        const rgb = color instanceof Array ? color : stringToColor(color);
        const opacity = props.fillOpacity !== undefined ? props.fillOpacity : 1;
        return [rgb[0], rgb[1], rgb[2], opacity];
    }
    /**
     * Return style state
     */
    getState() {
        return this._state;
    }
    /**
     * Return color of the stroke in [r, g, b, a] type
     */
    getStrokeRgba(props = undefined) {
        if (props === undefined) {
            props = this._state;
        }
        const color = props.stroke !== undefined ? props.stroke : props.color;
        const opacity = props.strokeOpacity !== undefined ? props.strokeOpacity : 1;
        return this.colorToRgba(color, opacity);
    }
    /**
     * Convert color to TRgba type
     */
    colorToRgba(color, opacity = 1) {
        const rgba = color instanceof Array ? color : stringToColor(color);
        if (!rgba) {
            return;
        }
        const _opacity = opacity !== undefined ? opacity : rgba[3] !== undefined ? rgba[3] : 1;
        return [rgba[0], rgba[1], rgba[2], _opacity];
    }
    /**
     * Compose object with icon parameters, where src is svg string
     */
    getSvgIcon() {
        if (!this._state.svgPath) {
            return undefined;
        }
        const svgPath = this._state.svgPath;
        const fillRgba = this.getFillRgba();
        const fillString = "rgb(" + fillRgba[0] + "," + fillRgba[1] + "," + fillRgba[2] + ")";
        const width = this._state.width;
        const height = this._state.height;
        const opacity = this._state.opacity;
        const anchor = this._state.anchor;
        let svg = '<svg width="' +
            width +
            '" height="' +
            height +
            '" fill="' +
            fillString +
            '" ';
        svg += 'viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">';
        svg += '<path d="' + svgPath + '"></path>';
        svg += "</svg>";
        return {
            anchor,
            opacity,
            src: "data:image/svg+xml;utf8," + svg,
            scale: 1,
        };
    }
    /**
     * Return instance of Text
     */
    getText() {
        if (this._state.text !== undefined) {
            return new Text(this._state.text);
        }
        else {
            return undefined;
        }
    }
    /**
     * Extend state by a given one
     */
    _extends(state) {
        if (!state) {
            return;
        }
        this._state = extend(this._state, state);
    }
    toOlStyle(geomType, scale = undefined, feature = undefined) {
        const typeStrLower = geomType.toString().toLowerCase();
        if (typeStrLower.indexOf("point") > -1) {
            let olPoint;
            try {
                olPoint = this._getPointOlStyle(scale, feature);
            }
            catch (error) {
                console.log(error);
            }
            return olPoint;
        }
        else if (typeStrLower.indexOf("linestring") > -1) {
            return this._getLineOlStyle();
        }
        else if (typeStrLower.indexOf("polygon") > -1) {
            return this._getPolygonOlStyle();
        }
        else {
            return undefined;
        }
    }
    toLegend(geomType, label) {
        return {
            style: this.toOlStyle(geomType),
            canvasSize: this._getCanvasSize(),
            label: label,
            maxScale: this._state.maxScale === undefined ? 0 : this._state.maxScale,
            minScale: this._state.minScale === undefined ? 0 : this._state.minScale,
        };
    }
    _getCanvasSize() {
        const styleProps = this.getState();
        let imgSize;
        if (styleProps.imgSize !== undefined) {
            imgSize = styleProps.imgSize;
        }
        else {
            imgSize = [
                styleProps.width + styleProps.strokeWidth * 2 + 5,
                styleProps.height + styleProps.strokeWidth * 2 + 5,
            ];
        }
        if (imgSize[0] < 25) {
            imgSize[0] = 25;
        }
        if (imgSize[1] < 20) {
            imgSize[1] = 20;
        }
        return imgSize;
    }
    _getPointOlStyle(scale = undefined, feature = undefined) {
        const styleProps = this.getState();
        const olStyle = new OlStyle();
        if (styleProps.type === "text") {
            const props = extend({
                size: 12,
                font: "sans-serif",
                face: "normal",
                displacement: [0, 0],
                strokeWidth: 2,
            }, styleProps);
            const fill = new FillStyle({
                color: asString(this.getFillRgba()),
            });
            const stroke = new StrokeStyle({
                color: asString(this.getStrokeRgba()),
                width: styleProps.strokeWidth,
            });
            const olImage = new OlText({
                text: styleProps.text,
                font: `${props.face} ${props.size}px ${props.font}`,
                fill: fill,
                stroke: stroke,
                offsetX: props.displacement[0],
                offsetY: props.displacement[1],
                rotation: styleProps.rotation,
            });
            olStyle.setText(olImage);
        }
        else if (styleProps.src) {
            styleProps.crossOrigin = "anonymous";
            if (styleProps.width !== undefined && styleProps.height !== undefined) {
                delete styleProps.scale;
            }
            olStyle.setImage(new IconStyle(styleProps));
        }
        else {
            const fillColor = this.getFillRgba();
            fillColor[3] = styleProps.fillOpacity;
            const strokeColor = this.getStrokeRgba();
            strokeColor[3] = styleProps.strokeOpacity;
            const stroke = new StrokeStyle({
                color: asString(strokeColor),
                width: styleProps.strokeWidth,
            });
            const fill = new FillStyle({
                color: asString(fillColor),
            });
            let olImage;
            switch (styleProps.shape) {
                case "triangle":
                    olImage = new OlRegularShape({
                        radius: (styleProps.width / 2) * Math.SQRT2,
                        points: 3,
                        stroke: stroke,
                        angle: styleProps.angle !== undefined
                            ? (styleProps.angle * Math.PI) / 180
                            : 0,
                        fill: fill,
                        rotation: styleProps.rotation,
                        displacement: styleProps.displacement,
                        scale: [1, styleProps.height / 2 / (styleProps.width / 2)],
                    });
                    break;
                case "rectangle":
                    olImage = new OlRegularShape({
                        radius: (styleProps.width / 2) * Math.SQRT2,
                        points: 4,
                        stroke: stroke,
                        angle: Math.PI / 4,
                        fill: fill,
                        rotation: styleProps.rotation,
                        displacement: styleProps.displacement,
                        scale: [1, styleProps.height / 2 / (styleProps.width / 2)],
                    });
                    break;
                case "circle":
                default:
                    olImage = new CircleStyle({
                        radius: styleProps.width / 2,
                        stroke: stroke,
                        displacement: styleProps.displacement,
                        fill: fill,
                    });
                    break;
            }
            olStyle.setImage(olImage);
        }
        if (styleProps.label !== undefined) {
            const label = extend({
                size: 12,
                font: "sans-serif",
                face: "normal",
                fill: "black",
                stroke: "white",
                strokeWidth: 2,
                displacement: [0, 0],
                textAlign: "center",
                textBaseline: "middle",
                justify: "left",
            }, styleProps.label);
            const fill = new FillStyle({
                color: asString(this.getFillRgba(label)),
            });
            const stroke = new StrokeStyle({
                color: asString(this.getStrokeRgba(label)),
                width: label.strokeWidth,
            });
            olStyle.setText(new OlText({
                text: strTemplate(label.text, {
                    scale: scale,
                    values: feature !== undefined ? getValues(feature) : {},
                }),
                font: `${label.face} ${label.size}px ${label.font}`,
                fill: fill,
                stroke: stroke,
                offsetX: label.displacement[0],
                offsetY: label.displacement[1],
                rotation: label.rotation,
                textAlign: label.textAlign,
                textBaseline: label.textBaseline,
                justify: label.justify,
            }));
        }
        return olStyle;
    }
    _getLineOlStyle() {
        const styleProps = this.getState();
        const olStyle = new OlStyle();
        const strokeColor = this.getStrokeRgba();
        strokeColor[3] = styleProps.strokeOpacity;
        olStyle.setStroke(new StrokeStyle({
            color: asString(strokeColor),
            width: styleProps.strokeWidth,
        }));
        return olStyle;
    }
    _getPolygonOlStyle() {
        const styleProps = this.getState();
        const olStyle = new OlStyle();
        const fillColor = this.getFillRgba();
        fillColor[3] = styleProps.fillOpacity;
        olStyle.setFill(new FillStyle({
            color: asString(fillColor),
        }));
        const strokeColor = this.getStrokeRgba();
        strokeColor[3] = styleProps.strokeOpacity;
        olStyle.setStroke(new StrokeStyle({
            color: asString(strokeColor),
            width: styleProps.strokeWidth,
        }));
        return olStyle;
    }
}
//# sourceMappingURL=Style.js.map